import { useState } from 'react';
import { X } from 'lucide-react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../store/authStore';

// Production values (hardcoded for reliability)
const API_URL = 'https://api.hitzlink.com/api';
const BASE_DOMAIN = 'hitzlink.com';

interface EditSlugModalProps {
  page: {
    id: string;
    slug: string;
    name: string;
  };
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (newSlug: string) => void;
}

export default function EditSlugModal({ page, isOpen, onClose, onSuccess }: EditSlugModalProps) {
  const { token } = useAuthStore();
  const { t } = useTranslation('dashboard');
  const [slug, setSlug] = useState(page.slug);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleSlugChange = (value: string) => {
    // Convert to lowercase and replace spaces/invalid chars with hyphens
    const cleaned = value
      .toLowerCase()
      .replace(/[^a-z0-9-]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
    
    setSlug(cleaned);
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (slug.length < 3) {
      setError(t('slug.minLength'));
      return;
    }
    
    if (slug.length > 50) {
      setError(t('slug.maxLength'));
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      if (!token) {
        throw new Error(t('slug.notAuthenticated'));
      }

      const response = await fetch(`${API_URL}/pages/${page.id}/slug`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ slug }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || t('slug.updateFailed'));
      }

      toast.success(t('slug.updateSuccess'));
      onSuccess(data.slug);
      onClose();
    } catch (err: any) {
      console.error('Update slug error:', err);
      setError(err.message || t('slug.updateFailed'));
      toast.error(err.message || t('slug.updateFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const fullUrl = `${slug}.${BASE_DOMAIN}`;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 relative">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header */}
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {t('slug.title')}
          </h2>
          <p className="text-sm text-gray-600">
            {t('slug.description')}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* URL Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('slug.label')}
            </label>
            <div className="relative">
              <input
                type="text"
                value={slug}
                onChange={(e) => handleSlugChange(e.target.value)}
                maxLength={50}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                placeholder={t('slug.placeholder')}
                disabled={isLoading}
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-400">
                {slug.length}/50
              </div>
            </div>
            {error && (
              <p className="mt-2 text-sm text-red-600">{error}</p>
            )}
          </div>

          {/* Preview */}
          <div className="bg-gray-50 rounded-xl p-4">
            <p className="text-xs text-gray-600 mb-1">{t('slug.fullUrl')}</p>
            <p className="text-sm font-mono text-gray-900 break-all">
              https://{fullUrl}
            </p>
          </div>

          {/* Requirements */}
          <div className="bg-blue-50 rounded-xl p-4">
            <p className="text-xs font-medium text-blue-900 mb-2">{t('slug.requirementsTitle')}</p>
            <ul className="text-xs text-blue-700 space-y-1">
              <li>• {t('slug.req1')}</li>
              <li>• {t('slug.req2')}</li>
              <li>• {t('slug.req3')}</li>
            </ul>
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-medium"
              disabled={isLoading}
            >
              {t('slug.cancel')}
            </button>
            <button
              type="submit"
              disabled={isLoading || !slug || slug === page.slug}
              className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-medium"
            >
              {isLoading ? t('slug.updating') : t('slug.apply')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

